import Head from 'next/head';
import {useRouter} from 'next/router';
import {HTMLAttributes, ReactNode, useEffect} from 'react';
import {getDocumentTitle} from '../../common/getDocumentTitle';
import {requestContext} from '../../common/requestContext';
import useTypedDispatch from '../../common/useTypedDispatch';
import useTypedSelector from '../../common/useTypedSelector';
import {initializeFooter} from '../../footer/FooterSlice';
import {initializeHeader, setHeader} from '../../header/HeaderSlice';
import {MetaHead} from '../../meta/MetaHead';
import {MetaInfo} from '../../meta/MetaInfo.interface';
import {MobileLayout} from './MobileLayout';
import {MobileLayoutConfig} from './MobileLayoutConfig.interface';
import {PcLayout} from './PcLayout';
import {PcLayoutConfig} from './PcLayoutConfig.interface';

interface Props extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  title: string;
  mobileLayoutConfig?: MobileLayoutConfig;
  pcLayoutConfig?: PcLayoutConfig;
  metaInfo?: MetaInfo;
}

// XXX: PagewebLayout과 구별할 수 있는 용어가 있으면 좋을 것 같다
export function Layout({
  children,
  title,
  mobileLayoutConfig,
  pcLayoutConfig,
  metaInfo,
  ...props
}: Props) {
  // 404, 500 등 내부 추가되는 페이지의 경우 pathParam 적용이 안되어있어 결과적으로 requestContext 없이 들어올 수 있습니다. 이런 엣지 케이스를 위해 기본값을 추가합니다. 대부분의 경우엔 이 값이 존재합니다
  const isMobile =
    requestContext?.requestInfo?.display?.isMobile === undefined
      ? true
      : requestContext.requestInfo.display.isMobile;
  const isWebView =
    requestContext?.requestInfo?.display?.isWebView === undefined
      ? false
      : requestContext.requestInfo.display.isWebView;
  const customHeaderTitle = useTypedSelector(state => state.header.customTitle);
  const dispatch = useTypedDispatch();
  const router = useRouter();

  useEffect(() => {
    function initializeLayoutHeaderAndFooter() {
      dispatch(initializeHeader());
      dispatch(initializeFooter());
    }
    router.events.on('routeChangeStart', initializeLayoutHeaderAndFooter);
    return () =>
      router.events.off('routeChangeStart', initializeLayoutHeaderAndFooter);
  }, [dispatch, router.events]);

  useEffect(() => {
    dispatch(
      setHeader({
        title,
        mobile: {
          currentMobileHeaderType: mobileLayoutConfig?.headerType,
          isExposed: mobileLayoutConfig?.shouldShowHeader,
        },
      })
    );
  }, [
    dispatch,
    mobileLayoutConfig?.headerType,
    mobileLayoutConfig?.shouldShowHeader,
    title,
  ]);

  // 페이지 제작시 만들어진 header Title보다 클라이언트에서 조작해준 커스텀 헤더타이틀을 우선합니다
  const headerTitle =
    customHeaderTitle || mobileLayoutConfig?.headerTitle || title;
  // 타이틀에 대한 우선순위가 적용된 mobileLayoutConfig
  const newMobileLayoutConfig = {
    ...mobileLayoutConfig,
    headerTitle,
  };
  const documentTitle = getDocumentTitle(headerTitle, metaInfo?.lang);

  useEffect(() => {
    if (isWebView) {
      window.KPApp?.setHeaderTitle?.(JSON.stringify({title: documentTitle}));
    }
  }, [documentTitle, isWebView]);

  /**
   * nextjs의 prerender 과정에서 에러가 발생해서 예외처리를 추가했습니다.
   */
  if (!requestContext.requestInfo) {
    return null;
  }

  return (
    <>
      <Head>
        <title>{documentTitle}</title>
      </Head>
      <MetaHead metaInfo={metaInfo} />
      {(isMobile || pcLayoutConfig?.shouldUseMobileHeader) && (
        <MobileLayout {...newMobileLayoutConfig} {...props}>
          {children}
        </MobileLayout>
      )}
      {!isMobile && !pcLayoutConfig?.shouldUseMobileHeader && (
        <PcLayout {...pcLayoutConfig} {...props}>
          {children}
        </PcLayout>
      )}
    </>
  );
}
